// src/App.tsx

import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import type { NavigationItem } from './types';
import './styles/main.css';
import './styles/header.css';
import Home from './pages/Home';
import PdfResources from './pages/PdfResources';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import OrderLookup from './pages/OrderLookup';
import Games from './pages/Games';
import Login from './pages/Login';
import Profile from './pages/Profile';
import Signup from './pages/Signup';
import AdminUsers from './pages/AdminUsers';
import AdminGameProducts from './pages/AdminGameProducts';
import AdminApps from './pages/AdminApps';
import AdminErrorReports from './pages/AdminErrorReports';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import MovieToolsLanding from './pages/MovieToolsLanding';
import { CartProvider, useCart } from './contexts/CartContext';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

const Services: React.FC = () => (
  <div className="section container">
    <h1>Services Page</h1>
    <p>Coming soon...</p>
  </div>
);

const About: React.FC = () => (
  <div className="section container">
    <h1>About Page</h1>
    <p>Coming soon...</p>
  </div>
);

const Contact: React.FC = () => (
  <div className="section container">
    <h1>Contact Page</h1>
    <p>Coming soon...</p>
  </div>
);

import Apps from './pages/Apps';
import Credits from './pages/Credits';
import AdminCredits from './pages/AdminCredits';
import AdminPayments from './pages/AdminPayments';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState<boolean>(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const { cartCount } = useCart();
  const { user, isAuthenticated, logout, isAdmin } = useAuth();

  const navigation: NavigationItem[] = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
    { name: 'Apps', href: '/apps' },
    { name: 'Games', href: '/games' },
    { name: 'Credits', href: '/credits' },
  ];

  const isActive = (path: string): boolean => location.pathname === path;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="header">
      <nav className="header-nav">
        <Link to="/" className="header-logo">
          <div className="header-logo-icon">
            <img 
              src="/images/owl-perched.jpg" 
              alt="Snowbird Industries Logo" 
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                objectFit: 'cover'
              }}
            />
          </div>
          <span className="header-logo-text">Snowbird Industries</span>
        </Link>

        <div className="header-nav-desktop">
          {navigation.map((item: NavigationItem) => (
            item.external ? (
              <a
                key={item.name}
                href={item.href}
                className="header-nav-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.name}
              </a>
            ) : (
              <Link
                key={item.name}
                to={item.href}
                className={`header-nav-link ${isActive(item.href) ? 'active' : ''}`}
              >
                {item.name}
              </Link>
            )
          ))}

          {/* Cart Icon */}
          <Link
            to="/cart"
            className="header-nav-link"
            style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            {cartCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                backgroundColor: '#ef4444',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold',
              }}>
                {cartCount}
              </span>
            )}
          </Link>

          <div className="user-menu" ref={userMenuRef}>
            <button
              type="button"
              className="user-menu-button"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              aria-haspopup="true"
              aria-expanded={isUserMenuOpen}
            >
              <span className="user-menu-icon">
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </span>
              <span className="user-menu-label">
                {isAuthenticated ? user?.name : 'Account'}
              </span>
            </button>
            <div className={`user-menu-dropdown ${isUserMenuOpen ? 'open' : ''}`}>
              {isAuthenticated ? (
                <>
                  <div className="user-menu-header">
                    Signed in as
                    <span>{user?.email}</span>
                  </div>
                  <Link
                    to="/profile"
                    className="user-menu-item"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin/users"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      User Management
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/admin/games"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Game Management
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/admin/apps"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      App Management
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/admin/credits"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Credits Management
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/admin/payments"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Payment Settings
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/admin/errors"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Error Reports
                    </Link>
                  )}
                  {isAdmin && (
                    <div
                      className="user-menu-env"
                      style={{
                        borderTop: '1px solid rgba(0,0,0,0.08)',
                        marginTop: '0.25rem',
                        paddingTop: '0.25rem',
                      }}
                    >
                      <div
                        style={{
                          padding: '0.4rem 1rem 0.2rem',
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: '#9ca3af',
                          fontWeight: 600,
                        }}
                      >
                        Environment
                      </div>
                      {[
                        { key: 'prod', label: 'Production', host: 'snowbirdindustries.us', color: '#16a34a' },
                        { key: 'dev', label: 'Dev', host: 'dev.snowbirdindustries.us', color: '#f59e0b' },
                        { key: 'beta', label: 'Beta', host: 'beta.snowbirdindustries.us', color: '#6366f1' },
                      ].map((env) => {
                        const isCurrent =
                          typeof window !== 'undefined' && window.location.hostname === env.host;
                        return (
                          <button
                            key={env.key}
                            type="button"
                            className="user-menu-item"
                            disabled={isCurrent}
                            onClick={() => {
                              setIsUserMenuOpen(false);
                              if (isCurrent) return;
                              // Switch tiers by jumping to the same path on the target
                              // host. Tiers have isolated auth, so the target will
                              // prompt its own login.
                              window.location.assign(
                                `https://${env.host}${window.location.pathname}${window.location.search}`
                              );
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              opacity: isCurrent ? 0.6 : 1,
                              cursor: isCurrent ? 'default' : 'pointer',
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: env.color,
                                flexShrink: 0,
                              }}
                            />
                            <span>{env.label}</span>
                            {isCurrent && (
                              <span
                                style={{
                                  marginLeft: 'auto',
                                  fontSize: '0.6rem',
                                  color: '#9ca3af',
                                  textTransform: 'uppercase',
                                }}
                              >
                                current
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <button
                    type="button"
                    className="user-menu-item"
                    onClick={() => {
                      logout();
                      setIsUserMenuOpen(false);
                    }}
                  >
                  Log Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="user-menu-item"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="user-menu-item"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  Create Account
                </Link>
              </>
            )}
          </div>
        </div>
        </div>

        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="header-mobile-button"
          aria-label="Toggle mobile menu"
        >
          <svg
            className={isMenuOpen ? 'hidden' : 'block'}
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </nav>

      <div className={`header-mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        <div className="header-mobile-nav">
          {navigation.map((item: NavigationItem) => (
            item.external ? (
              <a
                key={item.name}
                href={item.href}
                className="header-mobile-link"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </a>
            ) : (
              <Link
                key={item.name}
                to={item.href}
                className={`header-mobile-link ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            )
          ))}

          {/* Cart Link */}
          <Link
            to="/cart"
            className="header-mobile-link"
            onClick={() => setIsMenuOpen(false)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            Cart
            {cartCount > 0 && (
              <span style={{
                backgroundColor: '#ef4444',
                color: 'white',
                borderRadius: '12px',
                padding: '0.125rem 0.5rem',
                fontSize: '0.75rem',
                fontWeight: 'bold',
              }}>
                {cartCount}
              </span>
            )}
          </Link>
          {isAuthenticated ? (
            <>
              <Link
                to="/profile"
                className="header-mobile-link"
                onClick={() => setIsMenuOpen(false)}
              >
                Profile
              </Link>
              {isAdmin && (
                <Link
                  to="/admin/users"
                  className="header-mobile-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  User Management
                </Link>
              )}
              {isAdmin && (
                <Link
                  to="/admin/games"
                  className="header-mobile-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Game Management
                </Link>
              )}
              {isAdmin && (
                <Link
                  to="/admin/apps"
                  className="header-mobile-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  App Management
                </Link>
              )}
              {isAdmin && (
                <Link
                  to="/admin/credits"
                  className="header-mobile-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Credits Management
                </Link>
              )}
              <button
                type="button"
                className="header-mobile-link header-mobile-action"
                onClick={() => {
                  logout();
                  setIsMenuOpen(false);
                }}
              >
                Log Out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="header-mobile-link"
                onClick={() => setIsMenuOpen(false)}
              >
                Log In
              </Link>
              <Link
                to="/signup"
                className="header-mobile-link"
                onClick={() => setIsMenuOpen(false)}
              >
                Create Account
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-dark text-white">
      <div className="container" style={{ padding: '3rem 1rem' }}>
        <div className="grid grid-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <div>
            <div className="flex align-center mb-3" style={{ marginBottom: '1rem' }}>
              <div className="header-logo-icon">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span className="header-logo-text" style={{ marginLeft: '0.5rem' }}>
                Snowbird Industries
              </span>
            </div>
            <p className="text-secondary text-small">
              Strategic business consulting and process optimization for sustainable growth.
            </p>
          </div>

          <div>
            <h4 className="mb-3">Services</h4>
            <ul className="list-none">
              <li style={{ marginBottom: '0.5rem' }}>
                <Link to="/services" className="text-secondary" style={{ textDecoration: 'none' }}>
                  Strategic Planning
                </Link>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <Link to="/services" className="text-secondary" style={{ textDecoration: 'none' }}>
                  Process Excellence
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="mb-3">Get In Touch</h4>
            <p className="text-secondary text-small mb-3">Ready to transform your business?</p>
            <Link 
              to="/contact" 
              className="btn btn-primary"
              style={{ display: 'inline-block' }}
            >
              Schedule Consultation
            </Link>
          </div>
        </div>

        <div style={{ 
          borderTop: '1px solid #374151', 
          marginTop: '2rem', 
          paddingTop: '2rem', 
          textAlign: 'center' 
        }}>
          <p className="text-secondary text-small">
            &copy; {currentYear} Snowbird Industries. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

const App: React.FC = () => {
  // Non-prod environment banner: shown on dev/beta (and localhost as dev),
  // nothing on production. Keyed off the hostname so one build works per tier.
  const envBanner = (() => {
    if (typeof window === 'undefined') return null;
    const host = window.location.hostname;
    // Standard SI env detection — works for the main site (dev./beta.
    // subdomains) AND apps/games (dev-/beta- host prefixes).
    if (host === 'beta.snowbirdindustries.us' || host.startsWith('beta-'))
      return { label: 'Beta', color: '#6366f1' };
    if (
      host === 'dev.snowbirdindustries.us' ||
      host.startsWith('dev-') ||
      host === 'localhost' ||
      host === '127.0.0.1'
    )
      return { label: 'Dev', color: '#f59e0b' };
    return null;
  })();
  return (
    <Router>
      <CartProvider>
        <div
          className={envBanner ? 'has-env-banner' : undefined}
          style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
        >
          {envBanner && (
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1001,
                width: '100%',
                height: 24,
                lineHeight: '24px',
                background: envBanner.color,
                color: '#fff',
                textAlign: 'center',
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              {envBanner.label} Environment
            </div>
          )}
          <Header />
          <main style={{ flexGrow: 1 }}>
            <Routes>
              {/* Hostname-based root: when accessed via movietools.snowbirdindustries.us
                  the root path serves the Movie Tools landing page directly. */}
              <Route
                path="/"
                element={
                  typeof window !== 'undefined' &&
                  window.location.hostname === 'movietools.snowbirdindustries.us'
                    ? <MovieToolsLanding />
                    : <Home />
                }
              />
              <Route path="/services" element={<Services />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/apps/*" element={<Apps />} />
              <Route path="/games" element={<Games />} />
              <Route path="/credits" element={<Credits />} />
              <Route path="/pdf" element={<PdfResources />} />
              <Route path="/movietools" element={<MovieToolsLanding />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/games"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminGameProducts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/apps"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminApps />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/credits"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminCredits />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/payments"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminPayments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/errors"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminErrorReports />
                  </ProtectedRoute>
                }
              />
              <Route path="/cart" element={<Cart />} />
              <Route
                path="/checkout"
                element={
                  <ProtectedRoute>
                    <Checkout />
                  </ProtectedRoute>
                }
              />
              <Route path="/order-success" element={<OrderSuccess />} />
              <Route path="/orders" element={<OrderLookup />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </CartProvider>
    </Router>
  );
};

export default App;
